import Box from '@mui/material/Box';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import clsx from 'clsx';
import LinkComp from 'components/atoms/LinkComp';
import { BRAND_NAME } from 'constants/Enums';
import { COLOR_STORE } from 'constants/Icons';
import { useRouter } from 'next/router';
import { memo } from 'react';
import { getSellerData } from 'services/SellerService';
import { ImageFallbackStatic } from 'utils/ImageFallback';
import MonitorUtils from 'utils/MonitorUtils';
import useMobileV2 from 'zustand-lib/storeMobile';
import styles from './styles.module.css';

/*
 Internal Seller:
- Có Hóa Đơn => "MedX"
- Không hóa đơn => Không hiện thị
External Seller:
- Doanh nghiệp => hiện tên doanh nghiệp
- Cá nhân => "Đối tác của thuocsi"
*/

// storeSlugFromUrl lấy slug từ url Gian hàng — vendorInfo.slug có omitempty nên
// có thể vắng. 'https://thuocsi.vn/flagship-store/cty-ta' -> 'cty-ta'.
const storeSlugFromUrl = (url = '') => url.split('?')[0].replace(/\/+$/, '').split('/').pop();

/**
 * isOtherFlagshipStore: đang ở trang Gian hàng A nhưng SKU lại được trả về dưới
 * danh nghĩa Gian hàng B.
 *
 * Một SKU có thể mang tag của 2 Gian hàng (1 Gian hàng thường + 1 Gian hàng có
 * vendor_store_internal_config). Backend ưu tiên trả Gian hàng CÓ cấu hình, nên
 * trên trang Gian hàng còn lại, tên hiện trên card là của Gian hàng khác — khách
 * không biết ai đang bán SKU này. Trường hợp đó để TRỐNG tên.
 *
 * Chỉ ẩn TÊN. Giá, link, và dữ liệu Gian hàng dùng để thêm vào giỏ giữ nguyên.
 *
 * Không xác định được slug của một trong hai bên -> false (giữ hành vi cũ): thà
 * hiện tên như trước còn hơn ẩn oan.
 */
const isOtherFlagshipStore = ({ router, vendorInfo }) => {
  if (!router?.pathname?.startsWith('/flagship-store/')) return false;

  const currentStoreSlug = router?.query?.slug;
  const vendorStoreSlug = vendorInfo?.slug || storeSlugFromUrl(vendorInfo?.url || '');
  if (!currentStoreSlug || !vendorStoreSlug) return false;

  return currentStoreSlug !== vendorStoreSlug;
};

function SellerInfo({
  sellerInfo = {},
  tags = [],
  isProductCard = false,
  isWishlist = false,
  isChat = false,
  containerClass,
  isWebView = false,
  product,
}) {
  const partnerName = `Đối tác của ${BRAND_NAME}`;
  const isMobileV2 = useMobileV2((state) => state.isMobileV2());
  const router = useRouter();

  const {
    sellerName,
    fullNameSeller,
    isDisplayStore,
    linkSeller,
    linkStore,
    individualNonVatSellerName = '',
    isStore,
    statusStore,
  } = getSellerData({ seller: sellerInfo, tags });

  // hiển thị với seller là doanh nghiệp ngoài
  // todo: tracking monitor client
  const trackingMonitorClient = ({ url = linkSeller, reffererUrl = router.asPath }) => {
    MonitorUtils.sendPageEvent({ url, reffererUrl });
  };

  // hard code store
  if (product?.vendorInfo && Object.keys(product.vendorInfo).length > 0) {
    // Gian hàng trả về khác Gian hàng đang xem -> giữ đúng khung (không xô lệch
    // layout card) nhưng không hiện tên. Xem isOtherFlagshipStore.
    if (isOtherFlagshipStore({ router, vendorInfo: product.vendorInfo })) {
      return (
        <Grid
          className={clsx(
            styles.supplierTitle,
            containerClass,
            {
              [styles.supplierTitle_mv2]: isMobileV2,
            },
            isChat && styles.m0,
          )}
          item
          xs={12}
          container
          justifyContent="flex-start"
          direction="row"
        />
      );
    }

    return (
      <Grid
        className={clsx(
          styles.supplierTitle,
          containerClass,
          {
            [styles.supplierTitle_mv2]: isMobileV2,
          },
          isChat && styles.m0,
        )}
        item
        xs={12}
        container
        justifyContent="flex-start"
        direction="row"
      >
        <Tooltip disableTouchListener={isMobileV2} title={product?.vendorInfo?.name}>
          <Grid className={isWishlist ? styles.supplier_title_v2 : styles.name} item xs={12} container justifyContent="flex-start" direction="row">
            {!isProductCard ? (
              <strong>Cung cấp bởi: </strong>
            ) : (
              <ImageFallbackStatic width="14" height="16" src={COLOR_STORE} alt={product?.vendorInfo?.name} className={styles.image_logo} />
            )}
            <>
              {isWebView ? (
                <Box
                  className={clsx(
                    styles.link,
                    {
                      [styles.link_mv2]: isMobileV2,
                    },
                    isWishlist && styles.supplier_title_v2,
                    isChat && styles.sellerProduct,
                  )}
                  stopProp
                  style={{ fontSize: !isChat && 12 }}
                >
                  {product?.vendorInfo?.name}
                </Box>
              ) : (
                <LinkComp
                  target="_blank"
                  className={clsx(
                    styles.link,
                    {
                      [styles.link_mv2]: isMobileV2,
                    },
                    isWishlist && styles.supplier_title_v2,
                    isChat && styles.sellerProduct,
                  )}
                  stopProp
                  style={{ fontSize: !isChat && 12 }}
                  href={product?.vendorInfo?.url}
                  onClick={() => {
                    trackingMonitorClient({});
                  }}
                >
                  {product?.vendorInfo?.name}
                </LinkComp>
              )}
            </>
          </Grid>
        </Tooltip>
      </Grid>
    );
  }

  return (
    <Grid
      className={clsx(
        styles.supplierTitle,
        {
          [styles.supplierTitle_mv2]: isMobileV2,
        },
        isChat && styles.m0,
      )}
      item
      xs={12}
      container
      justifyContent="flex-start"
      direction="row"
    >
      {(individualNonVatSellerName || sellerName) && (
        <Tooltip disableTouchListener={isMobileV2} title={fullNameSeller}>
          <Grid className={isWishlist ? styles.supplier_title_v2 : styles.name} item xs={12} container justifyContent="flex-start" direction="row">
            {!isProductCard ? (
              <strong>Cung cấp bởi: </strong>
            ) : (
              <ImageFallbackStatic width="14" height="16" src={COLOR_STORE} alt={sellerName} className={styles.image_logo} />
            )}
            {statusStore === 'ACTIVE' ? (
              <>
                {isWebView ? (
                  <Box
                    stopProp
                    className={clsx(
                      styles.link,
                      {
                        [styles.link_mv2]: isMobileV2,
                      },
                      isWishlist && styles.supplier_title_v2,
                      isChat && styles.sellerProduct,
                    )}
                  >
                    {individualNonVatSellerName || sellerName}
                    {!isProductCard && (
                      <Tooltip title="Xem cửa hàng">
                        <OpenInNewIcon style={{ color: '#676F77', paddingTop: '10px', marginBottom: '-3px' }} />
                      </Tooltip>
                    )}
                  </Box>
                ) : (
                  <LinkComp
                    stopProp
                    target="_blank"
                    className={clsx(
                      styles.link,
                      {
                        [styles.link_mv2]: isMobileV2,
                      },
                      isWishlist && styles.supplier_title_v2,
                      isChat && styles.sellerProduct,
                    )}
                    href={isStore ? linkStore : linkSeller}
                    onClick={() => {
                      trackingMonitorClient({ url: isStore ? linkStore : linkSeller });
                    }}
                  >
                    {individualNonVatSellerName || sellerName}
                    {!isProductCard && (
                      <Tooltip title="Xem cửa hàng">
                        <OpenInNewIcon style={{ color: '#676F77', paddingTop: '10px', marginBottom: '-3px' }} />
                      </Tooltip>
                    )}
                  </LinkComp>
                )}
              </>
            ) : (
              <>
                {isDisplayStore ? (
                  <>
                    {isWebView ? (
                      <Box
                        stopProp
                        className={clsx(
                          styles.link,
                          {
                            [styles.link_mv2]: isMobileV2,
                          },
                          isWishlist && styles.supplier_title_v2,
                          isChat && styles.sellerProduct,
                        )}
                      >
                        {individualNonVatSellerName || sellerName}
                      </Box>
                    ) : (
                      <LinkComp
                        stopProp
                        target="_blank"
                        className={clsx(
                          styles.link,
                          {
                            [styles.link_mv2]: isMobileV2,
                          },
                          isWishlist && styles.supplier_title_v2,
                          isChat && styles.sellerProduct,
                        )}
                        href={linkSeller}
                        onClick={() => {
                          trackingMonitorClient({});
                        }}
                      >
                        {individualNonVatSellerName || sellerName}
                      </LinkComp>
                    )}
                  </>
                ) : (
                  <>
                    {isWebView ? (
                      <Box
                        stopProp
                        className={clsx(
                          styles.link,
                          {
                            [styles.link_mv2]: isMobileV2,
                          },
                          isWishlist && styles.supplier_title_v2,
                          sellerName === partnerName && styles.text_tf_none,
                          isChat && styles.sellerProduct,
                        )}
                      >
                        {/* {individualNonVatSellerName || sellerName} */}
                      </Box>
                    ) : (
                      <LinkComp
                        stopProp
                        target="_blank"
                        className={clsx(
                          styles.link,
                          {
                            [styles.link_mv2]: isMobileV2,
                          },
                          isWishlist && styles.supplier_title_v2,
                          sellerName === partnerName && styles.text_tf_none,
                          isChat && styles.sellerProduct,
                        )}
                        href={linkSeller}
                        onClick={() => {
                          trackingMonitorClient({});
                        }}
                      >
                        {individualNonVatSellerName || sellerName}
                      </LinkComp>
                    )}
                  </>
                )}
              </>
            )}
          </Grid>
        </Tooltip>
      )}
    </Grid>
  );
}

export default memo(SellerInfo);
